import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import type { TFunction } from 'i18next';

import { AccionTexto } from '#components/mobile/inicio/comunes';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { color, radio, texto } from '#components/mobile/ui/tokens';

import {
  leerCopiaInterna,
  listarCopiasInternas,
  puedeElegirCarpeta,
} from './almacen';
import type { ResumenCopia } from './almacen';
import { MAX_COPIAS_INTERNAS } from './automatica';
import type { DestinoCopia, FrecuenciaCopia } from './automatica';
import type { ResultadoAuto, useCopiaAutomatica } from './useCopiaAutomatica';
import { useCopiaSeguridad } from './useCopiaSeguridad';
import type { ResultadoCopia } from './useCopiaSeguridad';

type Copia = ReturnType<typeof useCopiaAutomatica>;

/** «Última copia: hoy / ayer / hace 3 días / nunca». */
export function textoUltimaCopia(t: TFunction, dias: number | null): string {
  if (dias === null) {
    return t('Last backup: never');
  }
  if (dias === 0) {
    return t('Last backup: today');
  }
  if (dias === 1) {
    return t('Last backup: yesterday');
  }
  return t('Last backup: {{count}} days ago', { count: dias });
}

/**
 * Hoja del ancla de Inicio. Tres casos:
 * - presupuesto también en la nube: ya es automática, solo se informa;
 * - solo en el móvil y sin elegir: «¿Dónde guardo tus copias?» (una vez);
 * - solo en el móvil y elegido: cada cuánto, «Hacer copia ahora» y las
 *   copias guardadas dentro de Rumbo.
 * Siempre queda «Enviar una copia a Drive» (la copia a mano de antes).
 */
export function HojaCopia({
  copia,
  onCerrar,
  onHecha,
}: {
  copia: Copia;
  onCerrar: () => void;
  onHecha: () => void;
}) {
  const { t } = useTranslation();
  const manual = useCopiaSeguridad('inicio-ancla');
  const [eligiendo, setEligiendo] = useState(copia.config === null);
  const [destino, setDestino] = useState<DestinoCopia>(
    copia.config?.destino ?? 'rumbo',
  );
  const [frecuencia, setFrecuencia] = useState<FrecuenciaCopia>(
    copia.config?.frecuencia ?? 'dia',
  );
  const [aviso, setAviso] = useState<ResultadoAuto | ResultadoCopia | null>(
    null,
  );
  const [ocupado, setOcupado] = useState(false);
  const [copias, setCopias] = useState<ResumenCopia[]>([]);
  const carpetas = puedeElegirCarpeta();

  const { presupuesto } = copia;
  const verCopias =
    !copia.enNube && !eligiendo && copia.config?.destino === 'rumbo';
  useEffect(() => {
    if (!verCopias || !presupuesto || copia.copiando) {
      return;
    }
    let vivo = true;
    listarCopiasInternas(presupuesto)
      .then(lista => vivo && setCopias(lista))
      .catch(() => vivo && setCopias([]));
    return () => {
      vivo = false;
    };
  }, [verCopias, presupuesto, copia.copiando]);

  const accion = async (
    hacer: () => Promise<ResultadoAuto | ResultadoCopia>,
  ) => {
    setOcupado(true);
    setAviso(null);
    const r = await hacer();
    setOcupado(false);
    setAviso(r);
    if (r === 'hecha' || r === 'compartida' || r === 'descargada') {
      onHecha();
    }
    return r;
  };

  const descargarInterna = async (id: string) => {
    const guardada = await leerCopiaInterna(id);
    if (!guardada) {
      return;
    }
    const datos = new Uint8Array(await guardada.datos.arrayBuffer());
    await window.Actual.saveFile(
      datos as unknown as Buffer,
      guardada.nombre,
      t('Make a backup'),
    );
  };

  const subtitulo = copia.enNube
    ? t('In the cloud · saved automatically')
    : textoUltimaCopia(t, copia.estado.dias);

  return (
    <Hoja aria-label={t('Backup')} data-testid="hoja-copia" onCerrar={onCerrar}>
      <View style={{ gap: 14, paddingTop: 6, paddingBottom: 6 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: color.accentSoft,
              color: color.accent,
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icono nombre={copia.enNube ? 'nube' : 'anchor'} size={20} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ ...texto.titulo, color: color.fg }}>
              {eligiendo && !copia.enNube
                ? t('Where do I save your backups?')
                : t('Backup')}
            </Text>
            <Text
              data-testid="hoja-copia-subtitulo"
              style={{ ...texto.secundario, color: color.fg2 }}
            >
              {eligiendo && !copia.enNube
                ? t('You choose once; after that they are made by themselves.')
                : subtitulo}
            </Text>
          </View>
        </View>

        {copia.enNube ? (
          <Caja>
            <Text style={{ fontWeight: 700, color: color.fg }}>
              <Trans>Automatic backup in the cloud</Trans>
            </Text>
            <Text style={{ ...texto.secundario, color: color.fg2 }}>
              <Trans>
                Your budget lives on the Rumbo server, which also keeps a
                separate encrypted copy every night. You do not have to do
                anything.
              </Trans>
            </Text>
          </Caja>
        ) : eligiendo ? (
          <>
            <Opcion
              testId="destino-rumbo"
              marcada={destino === 'rumbo'}
              onPress={() => setDestino('rumbo')}
              titulo={t('Inside Rumbo')}
              texto={t(
                'On this phone, without doing anything. It keeps the last {{count}} to go back if something goes wrong.',
                { count: MAX_COPIAS_INTERNAS },
              )}
            />
            {carpetas && (
              <Opcion
                testId="destino-carpeta"
                marcada={destino === 'carpeta'}
                onPress={() => setDestino('carpeta')}
                titulo={t('In a folder')}
                texto={t(
                  'You choose the folder, for example Downloads or one that syncs to Drive by itself.',
                )}
              />
            )}
            <SelectorFrecuencia
              valor={frecuencia}
              onChange={setFrecuencia}
              t={t}
            />
            <Boton
              bloque
              isDisabled={ocupado}
              onPress={() =>
                void accion(() => copia.elegir({ destino, frecuencia })).then(
                  r => {
                    if (r === 'hecha') {
                      setEligiendo(false);
                    }
                  },
                )
              }
            >
              {ocupado ? (
                <Trans>Preparing…</Trans>
              ) : (
                <Trans>Start backups</Trans>
              )}
            </Boton>
          </>
        ) : (
          <>
            <Caja>
              <Text style={{ fontWeight: 700, color: color.fg }}>
                <Trans>Automatic backup</Trans>
              </Text>
              <Text style={{ ...texto.secundario, color: color.fg2 }}>
                {copia.config?.destino === 'carpeta'
                  ? t('In the folder you chose on this phone')
                  : t('Inside Rumbo, on this phone')}
              </Text>
              <SelectorFrecuencia
                valor={copia.config?.frecuencia ?? 'dia'}
                onChange={f =>
                  copia.config &&
                  copia.setConfig({ ...copia.config, frecuencia: f })
                }
                t={t}
              />
              <View style={{ flexDirection: 'row', gap: 16, flexWrap: 'wrap' }}>
                <AccionTexto onPress={() => setEligiendo(true)}>
                  <Trans>Change where</Trans>
                </AccionTexto>
                <AccionTexto
                  onPress={() => {
                    copia.desactivar();
                    setEligiendo(true);
                  }}
                >
                  <Trans>Turn off</Trans>
                </AccionTexto>
              </View>
            </Caja>
            <Boton
              bloque
              isDisabled={ocupado || copia.copiando}
              onPress={() => void accion(copia.hacerAhora)}
            >
              {ocupado || copia.copiando ? (
                <Trans>Preparing…</Trans>
              ) : (
                <Trans>Make backup now</Trans>
              )}
            </Boton>
          </>
        )}

        <Boton
          variante="fantasma"
          bloque
          isDisabled={ocupado}
          onPress={() => void accion(manual.hacerCopia)}
        >
          <Trans>Send a copy to Drive</Trans>
        </Boton>

        {(aviso === 'cancelada' || aviso === 'fallo-compartir') && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ ...texto.secundario, color: color.fg2, flex: 1 }}>
              <Trans>
                The backup was not saved. You can download it to your phone
                instead.
              </Trans>
            </Text>
            <AccionTexto
              isDisabled={ocupado}
              onPress={() => void accion(manual.descargar)}
            >
              <Trans>Download</Trans>
            </AccionTexto>
          </View>
        )}
        {aviso === 'sin-permiso' && (
          <Text role="alert" style={{ ...texto.secundario, color: color.warn }}>
            <Trans>
              Rumbo can no longer write in the folder. Tap «Make backup now» to
              allow it again, or choose another place.
            </Trans>
          </Text>
        )}
        {aviso === 'error' && (
          <Text role="alert" style={{ ...texto.secundario, color: color.bad }}>
            <Trans>The backup could not be made. Try again later.</Trans>
          </Text>
        )}

        {verCopias && copias.length > 0 && (
          <View style={{ gap: 4 }} data-testid="copias-internas">
            <Text style={{ ...texto.secundario, fontWeight: 700 }}>
              {t('Saved backups ({{count}})', { count: copias.length })}
            </Text>
            {copias.map(c => (
              <View
                key={c.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <Text style={{ ...texto.secundario, color: color.fg }}>
                  {new Date(c.fecha).toLocaleString(undefined, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
                <AccionTexto onPress={() => void descargarInterna(c.id)}>
                  <Trans>Download</Trans>
                </AccionTexto>
              </View>
            ))}
          </View>
        )}

        {!copia.enNube && (
          <Text style={{ ...texto.secundario, color: color.fg3 }}>
            <Trans>
              Backups inside the phone are lost if you lose the phone: send one
              to Drive from time to time. To restore one: Switch budget → Import
              file → Budget file (.zip).
            </Trans>
          </Text>
        )}
      </View>
    </Hoja>
  );
}

function Caja({ children }: { children: ReactNode }) {
  return (
    <View
      style={{
        gap: 8,
        padding: 12,
        borderRadius: radio.boton,
        backgroundColor: color.surface2,
      }}
    >
      {children}
    </View>
  );
}

function Opcion({
  marcada,
  onPress,
  titulo,
  texto: descripcion,
  testId,
}: {
  marcada: boolean;
  onPress: () => void;
  titulo: string;
  texto: string;
  testId: string;
}) {
  return (
    <Button
      variant="bare"
      aria-pressed={marcada}
      data-testid={testId}
      onPress={onPress}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        gap: 2,
        padding: 12,
        borderRadius: radio.boton,
        backgroundColor: color.surface2,
        outline: marcada ? `2px solid ${color.accent}` : 'none',
        outlineOffset: -2,
      }}
    >
      <Text style={{ fontWeight: 700, color: color.fg }}>{titulo}</Text>
      <Text style={{ ...texto.secundario, color: color.fg2 }}>
        {descripcion}
      </Text>
    </Button>
  );
}

function SelectorFrecuencia({
  valor,
  onChange,
  t,
}: {
  valor: FrecuenciaCopia;
  onChange: (f: FrecuenciaCopia) => void;
  t: TFunction;
}) {
  return (
    <Segmentado
      etiqueta={t('How often')}
      testId="frecuencia-copia"
      estirar
      opciones={[
        { valor: 'dia', texto: t('Every day') },
        { valor: 'semana', texto: t('Every week') },
      ]}
      valor={valor}
      onChange={onChange}
    />
  );
}
